// @erp/ui/command-palette — ⌘K / Ctrl-K jump-to-page over the NAV TREE.
//
// Scope is deliberate and honest: this searches the pages this session can
// actually reach (the same permission-filtered NavModule[] the sidebar
// renders), NOT records. There is no book/patron/document index behind it yet,
// so it never pretends to have one — the placeholder says "pages", and a miss
// says so plainly. When a record-search endpoint exists it becomes a second
// group here rather than a second UI.
//
// Open state is a module-level signal (the helpMode idiom) so the header's
// button and the global key handler drive one source of truth; the component
// itself is mounted once by AppShell.
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import { CornerDownLeft, Search } from "lucide-solid";
import type { NavModule } from "@erp/auth-kit/nav";

const [paletteOpen, setPaletteOpen] = createSignal(false);

// ⌘K / Ctrl-K is registered ONCE per document, not once per mounted palette:
// with a per-instance listener two live instances toggle the same signal on a
// single keypress and it never opens. Idempotent — safe to call from every
// mount; the listener lives as long as the page, which is the shell's life.
let hotkeyBound = false;
function bindPaletteHotkey(): void {
  if (hotkeyBound || typeof window === "undefined") return;
  hotkeyBound = true;
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      setPaletteOpen((v) => !v);
    }
  });
}

/** Reactive: is the palette showing? */
export { paletteOpen };
export const openCommandPalette = (): void => {
  setPaletteOpen(true);
};
export const closeCommandPalette = (): void => {
  setPaletteOpen(false);
};

/** The platform-correct chord label — ⌘K on Apple, Ctrl K everywhere else. */
export function paletteChord(): string {
  const apple =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
  return apple ? "⌘K" : "Ctrl K";
}

export interface PaletteEntry {
  id: string;
  label: string;
  /** "Library › Catalogue" — the trail that disambiguates same-named leaves. */
  trail: string;
  to: string;
}

/** Flatten modules → sections → items (+ one child level) into jumpable rows. */
export function paletteEntries(modules: NavModule[]): PaletteEntry[] {
  const out: PaletteEntry[] = [];
  const seen = new Set<string>();
  for (const module of modules) {
    for (const section of module.sections) {
      for (const item of section.items) {
        // A parent with children renders as a toggle in the sidebar, so its own
        // `to` is a logical anchor rather than a page — only leaves are listed.
        const leaves = item.children && item.children.length > 0 ? item.children : [item];
        for (const leaf of leaves) {
          // A hidden leaf with a `$param` in its path is a matcher's target,
          // not a place to jump to; a literal hidden utility route (Zones,
          // Record offline payment) IS jumpable and stays listed.
          if (leaf.hidden && leaf.to.includes("$")) continue;
          if (seen.has(leaf.to)) continue;
          seen.add(leaf.to);
          const trail =
            leaves === item.children
              ? `${module.label} › ${section.label} › ${item.label}`
              : `${module.label} › ${section.label}`;
          out.push({ id: `${module.id}:${leaf.id}`, label: leaf.label, trail, to: leaf.to });
        }
      }
    }
  }
  return out;
}

/** Rank: label-prefix beats label-substring beats trail-only; ties keep nav order. */
export function filterEntries(entries: PaletteEntry[], query: string): PaletteEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  const scored: { entry: PaletteEntry; rank: number }[] = [];
  for (const entry of entries) {
    const label = entry.label.toLowerCase();
    const rank = label.startsWith(q) ? 0 : label.includes(q) ? 1 : entry.trail.toLowerCase().includes(q) ? 2 : -1;
    if (rank >= 0) scored.push({ entry, rank });
  }
  return scored.sort((a, b) => a.rank - b.rank).map((s) => s.entry);
}

export function CommandPalette(props: { modules: NavModule[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = createSignal("");
  const [cursor, setCursor] = createSignal(0);
  let input: HTMLInputElement | undefined;

  const results = createMemo(() => filterEntries(paletteEntries(props.modules), query()).slice(0, 12));

  bindPaletteHotkey();

  // Every open starts clean and focused — a palette that reopens on last
  // query is a palette you have to clear before you can use it.
  //
  // FOCUS RETURNS TO THE OPENER (2026-09-20, batch 19 — the keyboard tour).
  // The palette is drawn without <dialog>, so nothing restores focus for it
  // the way the native element does for Modal: on Escape the dialog was
  // removed and focus fell to <body>, which for a keyboard user means Tab
  // starts over from the top of the shell. The element focused at open —
  // the header's search button, the sidebar's search row, or whatever held
  // focus when ⌘K was pressed — is remembered and refocused after the
  // <Show> has taken the dialog down, on Escape, on a backdrop click and on
  // a chosen page alike (APG dialog: focus returns to the invoker).
  let opener: HTMLElement | null = null;
  createEffect(() => {
    if (paletteOpen()) {
      const active = document.activeElement;
      opener = active instanceof HTMLElement && active !== document.body ? active : null;
      setQuery("");
      setCursor(0);
      queueMicrotask(() => input?.focus());
      return;
    }
    const back = opener;
    opener = null;
    if (back) queueMicrotask(() => back.isConnected && back.focus());
  });

  const go = (entry: PaletteEntry | undefined): void => {
    if (!entry) return;
    closeCommandPalette();
    void navigate({ to: entry.to });
  };

  return (
    <Show when={paletteOpen()}>
      {/* Own overlay rather than <dialog>: the shell already owns a drawer
          dialog stack, and this must layer above it without fighting for the
          top layer. bg-overlay is THE scrim (DEV-GUIDE-002 §12.1 — black at
          Spectrum's overlay opacity per theme, the same token spectrum.css
          §10 gives .modal); it was neutral at 40 %, which on the darkest ramp
          is a WHITE wash (neutral = #ebebeb there), plus a blur Spectrum's
          underlay does not have. */}
      <div
        class="fixed inset-0 z-[60] flex items-start justify-center bg-overlay p-4 pt-[12vh]"
        role="presentation"
        onClick={(e) => {
          if (e.currentTarget === e.target) closeCommandPalette();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Jump to a page"
          class="w-full max-w-lg overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-raised"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              closeCommandPalette();
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              setCursor((c) => (results().length ? (c + 1) % results().length : 0));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setCursor((c) => (results().length ? (c - 1 + results().length) % results().length : 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              go(results()[cursor()]);
            }
          }}
        >
          {/* The search box is a QUIET textfield in Spectrum's terms — no border
              of its own, one hairline under the row — and a quiet field does not
              ring: keyboard focus draws a 2px underline 2px BELOW the field's
              edge instead (spectrum-css textfield `--quiet::after`:
              inset-block-end = -(gap + width), block-size = width, background =
              the indicator colour when focused). Ported as a pseudo-element on
              the row, in the shared geometry and colour from @erp/ui/tokens.css.
              A ring around the bare input would be clipped by the dialog's
              overflow-hidden at the top and cross the hairline at the bottom. */}
          <div class="relative flex items-center gap-2.5 border-b border-base-300 px-4 after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-[calc(var(--ui-focus-ring-gap)+var(--ui-focus-ring-width))] after:h-(--ui-focus-ring-width) after:content-[''] has-[input:focus-visible]:after:bg-(--ui-focus-ring)">
            <Search size={16} class="shrink-0 text-base-subtle" aria-hidden="true" />
            <input
              ref={input}
              id="command-palette-input"
              class="w-full bg-transparent py-3.5 text-sm outline-none placeholder:text-base-subtle"
              placeholder="Jump to a page…"
              aria-label="Jump to a page"
              autocomplete="off"
              spellcheck={false}
              value={query()}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setCursor(0);
              }}
            />
            <kbd class="kbd kbd-sm shrink-0">esc</kbd>
          </div>
          <Show
            when={results().length > 0}
            fallback={
              <p class="px-4 py-6 text-center text-sm text-base-subtle">
                No page matches “{query()}”. This searches pages you can open — not books or patrons.
              </p>
            }
          >
            <ul class="max-h-[45vh] overflow-y-auto p-2">
              <For each={results()}>
                {(entry, i) => (
                  <li>
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 rounded-field px-3 py-2 text-left"
                      classList={{ "bg-(--ui-state-selected) text-primary": i() === cursor() }}
                      aria-current={i() === cursor() ? "true" : undefined}
                      onMouseEnter={() => setCursor(i())}
                      onClick={() => go(entry)}
                    >
                      <span class="min-w-0 flex-1">
                        <span class="block truncate text-sm font-medium">{entry.label}</span>
                        <span class="block truncate text-xs text-base-subtle">{entry.trail}</span>
                      </span>
                      <Show when={i() === cursor()}>
                        <CornerDownLeft size={14} class="shrink-0 text-base-subtle" aria-hidden="true" />
                      </Show>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </div>
    </Show>
  );
}
